import { useState, useEffect, useRef } from 'react';

// Textfält sparas efter så här lång paus i skrivandet (och alltid vid blur)
export const TYPING_IDLE_MS = 2000;
// Val, chips och liständringar
export const CHANGE_DEBOUNCE_MS = 500;
// Väntan mellan omförsök efter misslyckad sparning
export const RETRY_DELAYS_MS = [2000, 5000, 10000, 30000];
// Hur länge "Sparat" syns intill fältet
export const SAVED_MARK_MS = 2000;

/**
 * Automatisk sparning: kö, fördröjning, omförsök, kvittenser och sparning när
 * fliken döljs eller sidan lämnas. Själva skrivningen står den som använder
 * hooken för (konsultprofilen, uppdragsbudgeten).
 *
 *   write()      skriver allt osparat. Returnerar kvittensnycklarna för det
 *                som sparades ([] = inget att spara). Vid fel: kasta, gärna
 *                med err.saveKeys (nycklarna som inte sparades).
 *   hasChanges() finns det något osparat?
 *   ready()      har startläget lästs in? Innan dess sparas inget.
 *
 * Funktionerna läses via refs, så de ser alltid det senaste läget.
 */
export function useAutosave({ write, hasChanges, ready = () => true, logLabel = 'Ändringarna' }) {
  const [fieldStatus, setFieldStatus] = useState({});
  const [inFlight, setInFlight] = useState(0);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [flushTick, setFlushTick] = useState(0);

  const fnRef = useRef({ write, hasChanges, ready });
  fnRef.current = { write, hasChanges, ready };
  const queueRef = useRef(Promise.resolve());
  const debounceRef = useRef(null);
  const retryRef = useRef({ timer: null, attempt: 0 });
  const sideRetryRef = useRef({});
  const markTimersRef = useRef({});
  const markCounterRef = useRef(0);

  // ── Kvittenser ──
  function mark(keys, state) {
    if (!keys.length) return;
    const n = ++markCounterRef.current;
    setFieldStatus(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = { state, n }; });
      return next;
    });
    if (state === 'error') return;
    keys.forEach(k => {
      clearTimeout(markTimersRef.current[k]);
      markTimersRef.current[k] = setTimeout(() => setFieldStatus(prev => {
        if (prev[k]?.n !== n) return prev;
        const { [k]: _, ...rest } = prev;
        return rest;
      }), SAVED_MARK_MS);
    });
  }

  // Fel som en lyckad sparning har rättat (allt osparat skrivs varje gång)
  function clearMainErrors() {
    setFieldStatus(prev => {
      const fixed = Object.keys(prev).filter(k =>
        prev[k].state === 'error' && !k.startsWith('side:'));
      if (!fixed.length) return prev;
      const next = { ...prev };
      fixed.forEach(k => { delete next[k]; });
      return next;
    });
  }

  function scheduleRetry() {
    const r = retryRef.current;
    clearTimeout(r.timer);
    const delay = RETRY_DELAYS_MS[Math.min(r.attempt, RETRY_DELAYS_MS.length - 1)];
    r.attempt += 1;
    r.timer = setTimeout(() => flush(), delay);
  }

  /** Sparar allt osparat nu. Resolvar när sparningen är klar (lyckad eller ej). */
  function flush() {
    clearTimeout(debounceRef.current);
    debounceRef.current = null;
    if (!fnRef.current.ready()) return queueRef.current;
    setInFlight(n => n + 1);
    queueRef.current = queueRef.current.then(async () => {
      try {
        const keys = await fnRef.current.write();
        clearTimeout(retryRef.current.timer);
        retryRef.current.attempt = 0;
        clearMainErrors();
        if (keys.length) {
          setLastSavedAt(new Date());
          mark(keys, 'saved');
        }
      } catch (err) {
        console.error(`${logLabel} kunde inte sparas`, err);
        mark(err.saveKeys?.length ? err.saveKeys : ['main'], 'error');
        scheduleRetry();
      } finally {
        setInFlight(n => n - 1);
      }
    });
    return queueRef.current;
  }

  /**
   * Begär sparning. 0 = direkt efter nästa render (så att nyss satt
   * state kommer med), annars efter en paus utan nya ändringar.
   */
  function schedule(delay) {
    clearTimeout(debounceRef.current);
    if (!delay) { setFlushTick(n => n + 1); return; }
    debounceRef.current = setTimeout(() => flush(), delay);
  }

  useEffect(() => {
    if (flushTick) flush();
  // flush läser allt via refs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushTick]);

  /** Sparning utanför huvuddokumentet, med kvittens på `key`. */
  function saveSide(key, sideWrite) {
    const k = 'side:' + key;
    clearTimeout(sideRetryRef.current[k]?.timer);
    setInFlight(n => n + 1);
    queueRef.current = queueRef.current.then(async () => {
      try {
        await sideWrite();
        delete sideRetryRef.current[k];
        setLastSavedAt(new Date());
        mark([k], 'saved');
      } catch (err) {
        console.error('Kunde inte spara', key, err);
        mark([k], 'error');
        const attempt = (sideRetryRef.current[k]?.attempt || 0);
        sideRetryRef.current[k] = {
          attempt: attempt + 1,
          timer: setTimeout(() => saveSide(key, sideWrite),
            RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]),
        };
      } finally {
        setInFlight(n => n - 1);
      }
    });
  }

  const failing = Object.values(fieldStatus).some(s => s.state === 'error');
  const failingRef = useRef(false);
  failingRef.current = failing;
  const changed = () => fnRef.current.ready() && fnRef.current.hasChanges();
  const isDirty = () => changed() || failingRef.current;

  // Dold flik, stängd flik eller lämnad sida: skicka det osparade direkt
  // (samma mönster som PublicOfferView)
  useEffect(() => {
    const markTimers = markTimersRef.current;
    const sideRetries = sideRetryRef.current;
    const retry = retryRef.current;
    const flushNow = () => { if (changed()) flush(); };
    const handleVisibility = () => { if (document.visibilityState === 'hidden') flushNow(); };
    const handleBeforeUnload = e => {
      flushNow();
      if (isDirty()) { e.preventDefault(); e.returnValue = ''; }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', flushNow);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      flushNow();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', flushNow);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearTimeout(debounceRef.current);
      clearTimeout(retry.timer);
      Object.values(markTimers).forEach(clearTimeout);
      Object.values(sideRetries).forEach(r => clearTimeout(r.timer));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    schedule, flush, saveSide, isDirty, setLastSavedAt,
    statusFor: key => fieldStatus[key],
    sideStatusFor: key => fieldStatus['side:' + key],
    failing, saving: inFlight > 0, lastSavedAt,
  };
}
